<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { VueFlow, MarkerType, ConnectionMode, useVueFlow, type Connection } from '@vue-flow/core'
import '@vue-flow/core/dist/style.css'
import '@vue-flow/core/dist/theme-default.css'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { MoveRight, RotateCcw, RotateCw, ZoomIn, ZoomOut, Maximize2, Trash2 } from 'lucide-vue-next'
import ProcessFlowNode from './ProcessFlowNode.vue'
import { cloneProcessGraph, PROCESS_NODE_TYPES, serializeProcessGraph, validateProcessGraph, type ProcessGraph } from '@/utils/processFlow'
import { useI18n } from 'vue-i18n'

const props = defineProps<{ modelValue: ProcessGraph; readonly?: boolean; saving?: boolean; linkedProcesses?: any[] }>()
const emit = defineEmits<{ (event: 'save', graph: ProcessGraph): void }>()
const { t } = useI18n()

const nodes = ref<any[]>([])
const edges = ref<any[]>([])
const selectedNode = ref<any>(null)
const selectedEdge = ref<any>(null)
const history = ref<ProcessGraph[]>([])
const future = ref<ProcessGraph[]>([])
const ready = ref(false)
const error = ref('')
let saveTimer: ReturnType<typeof setTimeout> | null = null
let nodeCounter = 0
const { zoomIn, zoomOut, fitView } = useVueFlow()

function loadGraph(graph: ProcessGraph) {
  ready.value = false
  const cloned = cloneProcessGraph(graph || { nodes: [], edges: [] })
  nodes.value = cloned.nodes || []
  edges.value = cloned.edges || []
  nodeCounter = nodes.value.length
  history.value = []
  future.value = []
  nextTick(() => { ready.value = true })
}

watch(() => props.modelValue, (graph) => {
  const incoming = cloneProcessGraph(graph || { nodes: [], edges: [] })
  if (ready.value && JSON.stringify(serializeProcessGraph(nodes.value, edges.value)) === JSON.stringify(incoming)) return
  loadGraph(incoming)
}, { immediate: true })

watch([nodes, edges], () => {
  if (!ready.value || props.readonly) return
  if (saveTimer) clearTimeout(saveTimer)
  saveTimer = setTimeout(() => {
    const graph = serializeProcessGraph(nodes.value, edges.value)
    const errors = validateProcessGraph(graph)
    error.value = errors.length ? errors.join(', ') : ''
    if (!errors.length) emit('save', graph)
  }, 900)
}, { deep: true })

onBeforeUnmount(() => { if (saveTimer) clearTimeout(saveTimer) })

function remember() {
  history.value.push(serializeProcessGraph(nodes.value, edges.value))
  if (history.value.length > 50) history.value.shift()
  future.value = []
}

function addNode(type: string) {
  if (props.readonly) return
  remember()
  nodeCounter += 1
  nodes.value.push({
    id: `node-${Date.now()}-${nodeCounter}`,
    type,
    position: { x: 80 + (nodeCounter % 5) * 150, y: 80 + Math.floor(nodeCounter / 5) * 120 },
    data: { label: typeLabel(type), linkedId: null },
  })
}

function connect(connection: Connection) {
  if (props.readonly || !connection.source || !connection.target) return
  remember()
  edges.value.push({
    id: `edge-${Date.now()}-${edges.value.length + 1}`,
    source: connection.source,
    target: connection.target,
    sourceHandle: connection.sourceHandle,
    targetHandle: connection.targetHandle,
    type: 'smoothstep',
    markerEnd: MarkerType.ArrowClosed,
    data: { label: '' },
  })
}

function undo() {
  const graph = history.value.pop()
  if (!graph) return
  future.value.push(serializeProcessGraph(nodes.value, edges.value))
  nodes.value = graph.nodes
  edges.value = graph.edges
}

function redo() {
  const graph = future.value.pop()
  if (!graph) return
  history.value.push(serializeProcessGraph(nodes.value, edges.value))
  nodes.value = graph.nodes
  edges.value = graph.edges
}

function removeSelection() {
  if (props.readonly) return
  remember()
  if (selectedNode.value) {
    const id = selectedNode.value.id
    nodes.value = nodes.value.filter((node) => node.id !== id)
    edges.value = edges.value.filter((edge) => edge.source !== id && edge.target !== id)
    selectedNode.value = null
  } else if (selectedEdge.value) {
    edges.value = edges.value.filter((edge) => edge.id !== selectedEdge.value.id)
    selectedEdge.value = null
  }
}

function typeLabel(type: string) {
  return t(`processManagement.nodeTypes.${type}`)
}
</script>

<template>
  <div class="space-y-3">
    <div v-if="!readonly" class="flex flex-wrap items-center gap-2 rounded-lg border bg-slate-50 p-2">
      <Button v-for="type in PROCESS_NODE_TYPES" :key="type" type="button" size="sm" variant="outline" @click="addNode(type)">
        {{ typeLabel(type) }}
      </Button>
      <span class="mx-1 h-6 border-l" />
      <Button type="button" size="icon" variant="ghost" :disabled="!history.length" @click="undo"><RotateCcw class="h-4 w-4" /></Button>
      <Button type="button" size="icon" variant="ghost" :disabled="!future.length" @click="redo"><RotateCw class="h-4 w-4" /></Button>
      <Button type="button" size="icon" variant="ghost" :disabled="!selectedNode && !selectedEdge" @click="removeSelection"><Trash2 class="h-4 w-4" /></Button>
      <span class="ml-auto text-xs text-slate-500">{{ saving ? t('processManagement.saving') : t('processManagement.autosaved') }}</span>
    </div>

    <p v-if="!readonly" class="flex items-center gap-2 rounded-lg border border-[#80BA27]/30 bg-[#80BA27]/10 px-3 py-2 text-sm text-slate-700">
      <MoveRight class="h-4 w-4 shrink-0 text-[#80BA27]" />
      {{ t('processManagement.flowConnectionHint') }}
    </p>

    <div class="relative h-[560px] overflow-hidden rounded-xl border bg-white">
      <VueFlow
        v-model:nodes="nodes"
        v-model:edges="edges"
        :connection-mode="ConnectionMode.Loose"
        :nodes-draggable="!readonly"
        :nodes-connectable="!readonly"
        :elements-selectable="true"
        fit-view-on-init
        @connect="connect"
        @node-drag-start="remember"
        @node-click="({ node }) => { selectedNode = node; selectedEdge = null }"
        @edge-click="({ edge }) => { selectedEdge = edge; selectedNode = null }"
        class="pm-flow-grid"
      >
        <template v-for="type in PROCESS_NODE_TYPES" #[`node-${type}`]="nodeProps" :key="type">
          <ProcessFlowNode v-bind="nodeProps" :type="type" />
        </template>
      </VueFlow>
      <div class="absolute bottom-3 left-3 z-10 flex gap-1 rounded-md border bg-white p-1 shadow">
        <Button type="button" size="icon" variant="ghost" @click="zoomIn"><ZoomIn class="h-4 w-4" /></Button>
        <Button type="button" size="icon" variant="ghost" @click="zoomOut"><ZoomOut class="h-4 w-4" /></Button>
        <Button type="button" size="icon" variant="ghost" @click="fitView"><Maximize2 class="h-4 w-4" /></Button>
      </div>
    </div>

    <div v-if="!readonly && (selectedNode || selectedEdge)" class="rounded-lg border p-3">
      <label class="mb-1 block text-xs font-medium text-slate-600">{{ t('processManagement.label') }}</label>
      <Input v-if="selectedNode" v-model="selectedNode.data.label" maxlength="500" @focus="remember" />
      <Input v-else v-model="selectedEdge.data.label" maxlength="500" @focus="remember" />
      <template v-if="selectedNode?.type === 'process'">
        <label class="mb-1 mt-3 block text-xs font-medium text-slate-600">{{ t('processManagement.linkedProcess') }}</label>
        <select v-model.number="selectedNode.data.linkedId" class="h-10 w-full rounded-md border bg-white px-3" @focus="remember">
          <option :value="null">—</option>
          <option v-for="item in linkedProcesses" :key="item.id" :value="item.id">{{ item.code }} · {{ item.title }}</option>
        </select>
      </template>
    </div>
    <p v-if="error" class="text-sm text-red-600">{{ error }}</p>
  </div>
</template>

<style>
.pm-flow-grid {
  --vf-connection-path: #80ba27;
  --vf-handle: #80ba27;
  background-color: #f8fafc;
  background-image: radial-gradient(#cbd5e1 1px, transparent 1px);
  background-size: 18px 18px;
}
.pm-flow-grid .pm-flow-handle {
  width: 20px;
  height: 20px;
  min-width: 20px;
  min-height: 20px;
  border: 0;
  background: transparent;
  box-shadow: none;
}
.pm-flow-grid .pm-flow-handle::after {
  content: '';
  position: absolute;
  inset: 6px;
  border-radius: 50%;
  border: 1px solid #fff;
  background: #80ba27;
  box-shadow: 0 0 0 1px #5f8f1b;
  pointer-events: none;
  transition: background-color 150ms ease, box-shadow 150ms ease;
}
.pm-flow-grid .pm-flow-handle.connectable {
  cursor: crosshair;
}
.pm-flow-grid .pm-flow-handle.connectable:hover::after,
.pm-flow-grid .pm-flow-handle.connecting::after,
.pm-flow-grid .pm-flow-handle.valid::after {
  background: #9cd33d;
  box-shadow: 0 0 0 1px #5f8f1b, 0 0 0 3px rgb(128 186 39 / 20%);
}
.pm-flow-grid .vue-flow__edge-path {
  stroke: #64748b;
  stroke-width: 2;
}
.pm-flow-grid .vue-flow__edge.selected .vue-flow__edge-path,
.pm-flow-grid .vue-flow__edge:focus .vue-flow__edge-path {
  stroke: #80ba27;
  stroke-width: 2.5;
}
.vue-flow__edge-text { font-size: 11px; }
</style>
